import React, { useMemo, useState, useEffect } from 'react';
import {
  CustomFieldDefinitionInput,
  CustomFieldType,
} from '../../graphql/generatedTypes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  FaPlus,
  FaTrash,
  FaChevronDown,
  FaChevronRight,
  FaSave,
} from 'react-icons/fa';
import { cn } from '@/lib/utils';
import i18n from '@/utils/i18n';
import { generateFieldId } from '@/utils/random';

const NONE_VALUE = '__none__';

interface ItemTypeCustomFieldsProps {
  value: CustomFieldDefinitionInput[];
  onChange: (fields: CustomFieldDefinitionInput[]) => void;
  editable?: boolean;
  lockedFieldIds?: Set<string>;
}

const FIELD_TYPES: CustomFieldType[] = [
  CustomFieldType.String,
  CustomFieldType.Integer,
  CustomFieldType.Number,
  CustomFieldType.Boolean,
];

function getFieldTypeLabel(type: CustomFieldType): string {
  switch (type) {
    case CustomFieldType.String:
      return i18n.t('ItemType:fieldType.text', 'Text');
    case CustomFieldType.Integer:
      return i18n.t('ItemType:fieldType.wholeNumber', 'Whole Number');
    case CustomFieldType.Number:
      return i18n.t('ItemType:fieldType.decimal', 'Decimal');
    case CustomFieldType.Boolean:
      return i18n.t('ItemType:fieldType.yesNo', 'Yes/No');
    default:
      return type;
  }
}

function validateDefaultValue(
  value: string | undefined | null,
  type: CustomFieldType,
): boolean {
  if (!value) return true;

  switch (type) {
    case CustomFieldType.String:
      return true;
    case CustomFieldType.Integer:
      return /^\d+$/.test(value);
    case CustomFieldType.Number:
      return /^\d+([.,]\d*)?$/.test(value);
    case CustomFieldType.Boolean:
      return value === 'true' || value === 'false';
    default:
      return true;
  }
}

function getDefaultValuePlaceholder(type: CustomFieldType): string {
  switch (type) {
    case CustomFieldType.String:
      return 'Any text';
    case CustomFieldType.Integer:
      return 'e.g. 42';
    case CustomFieldType.Number:
      return 'e.g. 3,14 or 3.14';
    case CustomFieldType.Boolean:
      return i18n.t('Common:yesOrNo', 'Yes or No');
    default:
      return '';
  }
}

// Type-aware input for condition values based on parent field type
function ConditionValueInput({
  parentType,
  value,
  onChange,
  editable,
}: {
  parentType: CustomFieldType;
  value: string;
  onChange: (value: string) => void;
  editable?: boolean;
}) {
  switch (parentType) {
    case CustomFieldType.Boolean:
      return (
        <Select
          value={value || NONE_VALUE}
          onValueChange={(val) => onChange(val === NONE_VALUE ? '' : val)}
          disabled={!editable}
        >
          <SelectTrigger size="sm" className="tw:w-[100px]">
            <SelectValue placeholder={i18n.t('Common:select', 'Select')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">{i18n.t('Common:yes', 'Yes')}</SelectItem>
            <SelectItem value="false">{i18n.t('Common:no', 'No')}</SelectItem>
          </SelectContent>
        </Select>
      );
    case CustomFieldType.Integer:
      return (
        <Input
          type="number"
          step="1"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ''))}
          placeholder="e.g. 42"
          className="tw:h-8 tw:w-[100px]"
          disabled={!editable}
        />
      );
    case CustomFieldType.Number:
      return (
        <Input
          type="number"
          step="any"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. 3.14"
          className="tw:h-8 tw:w-[100px]"
          disabled={!editable}
        />
      );
    default:
      return (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={i18n.t('ItemType:expectedValue', 'Expected value')}
          className="tw:h-8 tw:w-[120px]"
          disabled={!editable}
        />
      );
  }
}

// Field card for conditional (nested) fields
function ConditionalFieldCard({
  editable,
  locked,
  field,
  parentField,
  onUpdate,
  onRemove,
  onUpdateConditionValue,
}: {
  editable?: boolean;
  locked?: boolean;
  field: CustomFieldDefinitionInput;
  parentField: CustomFieldDefinitionInput;
  onUpdate: (updates: Partial<CustomFieldDefinitionInput>) => void;
  onRemove: () => void;
  onUpdateConditionValue: (value: string) => void;
}) {
  const isDefaultValueValid = validateDefaultValue(
    field.default_value,
    field.type,
  );
  const disabled = !editable || locked;

  return (
    <div className="tw:border tw:border-border tw:rounded-md tw:p-3 tw:space-y-3 tw:bg-background tw:border-l-4 tw:border-l-primary/40">
      {/* Condition badge */}
      <div className="tw:flex tw:items-center tw:gap-2 tw:flex-wrap">
        <Badge variant="secondary" className="tw:text-xs tw:font-normal">
          {i18n.t('ItemType:showWhen', 'Show when')}:{' '}
          {parentField.name || '(unnamed)'} =
        </Badge>
        <ConditionValueInput
          parentType={parentField.type}
          value={field.depends_on?.value ?? ''}
          onChange={onUpdateConditionValue}
          editable={!disabled}
        />
      </div>

      {/* Row 1: Name + Type + Required + Remove */}
      <div className="tw:flex tw:items-start tw:gap-3 tw:flex-wrap">
        {/* Remove button */}
        {editable && !locked && (
          <div className="tw:pt-5">
            <Button
              type="button"
              variant="destructive"
              size="icon-sm"
              onClick={onRemove}
              title={i18n.t('Common:remove', 'Remove')}
            >
              <FaTrash />
            </Button>
          </div>
        )}

        {/* Name */}
        <div className="tw:flex-1 tw:min-w-[140px]">
          <Label className="tw:text-xs tw:text-muted-foreground tw:mb-1">
            {i18n.t('Common:name', 'Name')}
          </Label>
          <Input
            value={field.name}
            onChange={(e) => onUpdate({ name: e.target.value })}
            placeholder={i18n.t('ItemType:fieldName', 'Field name')}
            className="tw:h-8"
            disabled={disabled}
          />
        </div>

        {/* Type */}
        <div className="tw:w-[120px]">
          <Label className="tw:text-xs tw:text-muted-foreground tw:mb-1">
            {i18n.t('Common:type', 'Type')}
          </Label>
          <Select
            value={field.type}
            onValueChange={(val) => onUpdate({ type: val as CustomFieldType })}
            disabled={disabled}
          >
            <SelectTrigger size="sm" className="tw:w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FIELD_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {getFieldTypeLabel(t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Required */}
        <div className="tw:flex tw:flex-col tw:items-center tw:pt-5">
          <Label className="tw:text-xs tw:text-muted-foreground tw:mb-1">
            {i18n.t('Common:required', 'Required')}
          </Label>
          <Checkbox
            checked={field.required ?? false}
            onCheckedChange={(checked) => onUpdate({ required: !!checked })}
            disabled={disabled}
          />
        </div>
      </div>

      {/* Row 2: Default Value */}
      <div className="tw:max-w-[300px]">
        <Label className="tw:text-xs tw:text-muted-foreground tw:mb-1">
          {i18n.t('ItemType:defaultValue', 'Default Value')}
        </Label>
        {field.type === CustomFieldType.Boolean ? (
          <Select
            value={field.default_value ?? NONE_VALUE}
            onValueChange={(val) =>
              onUpdate({ default_value: val === NONE_VALUE ? undefined : val })
            }
            disabled={disabled}
          >
            <SelectTrigger size="sm" className="tw:w-full">
              <SelectValue placeholder={i18n.t('Common:none', 'None')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>
                {i18n.t('Common:none', 'None')}
              </SelectItem>
              <SelectItem value="true">
                {i18n.t('Common:yes', 'Yes')}
              </SelectItem>
              <SelectItem value="false">{i18n.t('Common:no', 'No')}</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <Input
            value={field.default_value ?? ''}
            onChange={(e) =>
              onUpdate({ default_value: e.target.value || undefined })
            }
            placeholder={getDefaultValuePlaceholder(field.type)}
            className={cn(
              'tw:h-8',
              !isDefaultValueValid && 'tw:border-destructive',
            )}
            aria-invalid={!isDefaultValueValid}
            disabled={disabled}
          />
        )}
        {!isDefaultValueValid && (
          <p className="tw:text-destructive tw:text-xs tw:mt-1">
            {i18n.t(
              'ItemType:invalidDefaultValue',
              'Invalid value for this type',
            )}
          </p>
        )}
      </div>
    </div>
  );
}

// Main field card (for top-level/parent fields)
function FieldCard({
  editable,
  lockedFieldIds,
  field,
  conditionalFields,
  onUpdate,
  onRemove,
  onAddConditionalField,
  onUpdateConditionalField,
  onRemoveConditionalField,
  onUpdateConditionValue,
}: {
  editable?: boolean;
  lockedFieldIds?: Set<string>;
  field: CustomFieldDefinitionInput;
  conditionalFields: CustomFieldDefinitionInput[];
  onUpdate: (updates: Partial<CustomFieldDefinitionInput>) => void;
  onRemove: () => void;
  onAddConditionalField: () => void;
  onUpdateConditionalField: (
    id: string,
    updates: Partial<CustomFieldDefinitionInput>,
  ) => void;
  onRemoveConditionalField: (id: string) => void;
  onUpdateConditionValue: (childId: string, value: string) => void;
}) {
  const [isSubfieldsOpen, setIsSubfieldsOpen] = React.useState(
    conditionalFields.length > 0,
  );
  const isDefaultValueValid = validateDefaultValue(
    field.default_value,
    field.type,
  );
  const hasConditionalFields = conditionalFields.length > 0;
  const locked = lockedFieldIds?.has(field.id);
  const disabled = !editable || locked;

  return (
    <div className="tw:border tw:border-border tw:rounded-md tw:bg-muted/30">
      <div className="tw:p-3 tw:space-y-3">
        {/* Row 1: Name + Type + Required + Remove */}
        <div className="tw:flex tw:items-start tw:gap-3 tw:flex-wrap">
          {/* Remove button */}
          {editable && !locked && (
            <div className="tw:pt-5">
              <Button
                type="button"
                variant="destructive"
                size="icon-sm"
                onClick={onRemove}
                title={i18n.t('Common:remove', 'Remove')}
              >
                <FaTrash />
              </Button>
            </div>
          )}
          {/* Name */}
          <div className="tw:flex-1 tw:min-w-[140px]">
            <Label className="tw:text-xs tw:text-muted-foreground tw:mb-1">
              {i18n.t('Common:name', 'Name')}
            </Label>
            <Input
              value={field.name}
              onChange={(e) => onUpdate({ name: e.target.value })}
              placeholder={i18n.t('ItemType:fieldName', 'Field name')}
              className="tw:h-8"
              disabled={disabled}
            />
          </div>

          {/* Type */}
          <div className="tw:w-[120px]">
            <Label className="tw:text-xs tw:text-muted-foreground tw:mb-1">
              {i18n.t('Common:type', 'Type')}
            </Label>
            <Select
              value={field.type}
              onValueChange={(val) =>
                onUpdate({ type: val as CustomFieldType })
              }
              disabled={disabled}
            >
              <SelectTrigger size="sm" className="tw:w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FIELD_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {getFieldTypeLabel(t)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Required */}
          <div className="tw:flex tw:flex-col tw:items-center tw:pt-5">
            <Label className="tw:text-xs tw:text-muted-foreground tw:mb-1">
              {i18n.t('Common:required', 'Required')}
            </Label>
            <Checkbox
              checked={field.required ?? false}
              onCheckedChange={(checked) => onUpdate({ required: !!checked })}
              disabled={disabled}
            />
          </div>
        </div>

        {/* Row 2: Default Value */}
        <div className="tw:max-w-[300px]">
          <Label className="tw:text-xs tw:text-muted-foreground tw:mb-1">
            {i18n.t('ItemType:defaultValue', 'Default Value')}
          </Label>
          {field.type === CustomFieldType.Boolean ? (
            <Select
              value={field.default_value ?? NONE_VALUE}
              onValueChange={(val) =>
                onUpdate({
                  default_value: val === NONE_VALUE ? undefined : val,
                })
              }
              disabled={disabled}
            >
              <SelectTrigger size="sm" className="tw:w-full">
                <SelectValue placeholder={i18n.t('Common:none', 'None')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>
                  {i18n.t('Common:none', 'None')}
                </SelectItem>
                <SelectItem value="true">
                  {i18n.t('Common:yes', 'Yes')}
                </SelectItem>
                <SelectItem value="false">
                  {i18n.t('Common:no', 'No')}
                </SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={field.default_value ?? ''}
              onChange={(e) =>
                onUpdate({ default_value: e.target.value || undefined })
              }
              placeholder={getDefaultValuePlaceholder(field.type)}
              className={cn(
                'tw:h-8',
                !isDefaultValueValid && 'tw:border-destructive',
              )}
              aria-invalid={!isDefaultValueValid}
              disabled={disabled}
            />
          )}
          {!isDefaultValueValid && (
            <p className="tw:text-destructive tw:text-xs tw:mt-1">
              {i18n.t(
                'ItemType:invalidDefaultValue',
                'Invalid value for this type',
              )}
            </p>
          )}
        </div>
      </div>

      {/* Collapsible conditional sub-fields section */}
      <Collapsible open={isSubfieldsOpen} onOpenChange={setIsSubfieldsOpen}>
        <div className="tw:border-t tw:border-border tw:px-3 tw:py-2">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-muted-foreground tw:hover:text-foreground tw:transition-colors tw:w-full"
            >
              {isSubfieldsOpen ? (
                <FaChevronDown className="tw:w-3 tw:h-3" />
              ) : (
                <FaChevronRight className="tw:w-3 tw:h-3" />
              )}
              <span>
                {i18n.t(
                  'ItemType:conditionalSubfields',
                  'Conditional sub-fields',
                )}
              </span>
              {hasConditionalFields && (
                <Badge variant="secondary" className="tw:text-xs tw:ml-1">
                  {conditionalFields.length}
                </Badge>
              )}
            </button>
          </CollapsibleTrigger>
        </div>

        <CollapsibleContent>
          <div className="tw:px-3 tw:pb-3 tw:space-y-3">
            {/* Nested conditional fields */}
            <div className="tw:pl-4 tw:space-y-2">
              {conditionalFields.map((childField) => (
                <ConditionalFieldCard
                  key={childField.id}
                  editable={editable}
                  locked={lockedFieldIds?.has(childField.id)}
                  field={childField}
                  parentField={field}
                  onUpdate={(updates) =>
                    onUpdateConditionalField(childField.id, updates)
                  }
                  onRemove={() => onRemoveConditionalField(childField.id)}
                  onUpdateConditionValue={(value) =>
                    onUpdateConditionValue(childField.id, value)
                  }
                />
              ))}

              {/* Add conditional field button */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onAddConditionalField}
                className="tw:w-full tw:border-dashed"
                disabled={!editable}
              >
                <FaPlus className="tw:mr-1 tw:w-3 tw:h-3" />
                {i18n.t(
                  'ItemType:addConditionalField',
                  'Add conditional field',
                )}
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

export function EditItemTypeCustomFields({
  value,
  onChange,
  editable,
  lockedFieldIds,
}: ItemTypeCustomFieldsProps) {
  // Internal state - only synced to parent on submit
  const [internalFields, setInternalFields] =
    useState<CustomFieldDefinitionInput[]>(value);

  // Sync internal state when external value changes (e.g., initial load or reset)
  useEffect(() => {
    setInternalFields(value);
  }, [value]);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    return JSON.stringify(internalFields) !== JSON.stringify(value);
  }, [internalFields, value]);

  // Separate top-level fields from conditional fields
  const { topLevelFields, conditionalFieldsByParent } = useMemo(() => {
    const topLevel: CustomFieldDefinitionInput[] = [];
    const byParent: Record<string, CustomFieldDefinitionInput[]> = {};

    internalFields.forEach((field) => {
      if (field.depends_on?.field_id) {
        const parentId = field.depends_on.field_id;
        if (!byParent[parentId]) {
          byParent[parentId] = [];
        }
        byParent[parentId].push(field);
      } else {
        topLevel.push(field);
      }
    });

    return { topLevelFields: topLevel, conditionalFieldsByParent: byParent };
  }, [internalFields]);

  const addField = () => {
    const newField: CustomFieldDefinitionInput = {
      id: generateFieldId(),
      name: '',
      type: CustomFieldType.String,
      required: false,
      default_value: undefined,
      depends_on: undefined,
    };
    setInternalFields([...internalFields, newField]);
  };

  const addConditionalField = (parentId: string) => {
    const newField: CustomFieldDefinitionInput = {
      id: generateFieldId(),
      name: '',
      type: CustomFieldType.String,
      required: false,
      default_value: undefined,
      depends_on: { field_id: parentId, value: '' },
    };
    setInternalFields([...internalFields, newField]);
  };

  const removeField = (id: string) => {
    if (lockedFieldIds?.has(id)) return;
    // Also remove any fields that depend on this one (skip locked children)
    const updated = internalFields.filter((f) =>
      f.id === id ||
      (f.depends_on?.field_id === id && !lockedFieldIds?.has(f.id))
        ? false
        : true,
    );
    setInternalFields(updated);
  };

  const updateField = (
    id: string,
    updates: Partial<CustomFieldDefinitionInput>,
  ) => {
    setInternalFields(
      internalFields.map((f) => {
        if (f.id !== id) return f;

        const updated = { ...f, ...updates };

        // Clear default_value if type changes and value is invalid
        if (
          updates.type &&
          !validateDefaultValue(updated.default_value, updates.type)
        ) {
          updated.default_value = undefined;
        }

        return updated;
      }),
    );
  };

  const updateConditionValue = (childId: string, conditionValue: string) => {
    setInternalFields(
      internalFields.map((f) => {
        if (f.id !== childId || !f.depends_on) return f;
        return {
          ...f,
          depends_on: { ...f.depends_on, value: conditionValue },
        };
      }),
    );
  };

  const handleSubmit = () => {
    onChange(internalFields);
  };

  const handleReset = () => {
    setInternalFields(value);
  };

  return (
    <div className="tw:space-y-4">
      <div className="tw:flex tw:items-center tw:justify-between">
        <Label className="tw:text-base tw:font-semibold">
          {i18n.t('ItemType:customFields', 'Custom Fields')}
        </Label>
        <Button type="button" size="sm" onClick={addField} disabled={!editable}>
          <FaPlus className="tw:mr-1" />
          {i18n.t('ItemType:addField', 'Add Field')}
        </Button>
      </div>

      {topLevelFields.length === 0 ? (
        <p className="tw:text-muted-foreground tw:text-sm">
          {i18n.t('ItemType:noCustomFields', 'No custom fields defined.')}
        </p>
      ) : (
        <div className="tw:space-y-3">
          {topLevelFields.map((field) => (
            <FieldCard
              key={field.id}
              editable={editable}
              lockedFieldIds={lockedFieldIds}
              field={field}
              conditionalFields={conditionalFieldsByParent[field.id] || []}
              onUpdate={(updates) => updateField(field.id, updates)}
              onRemove={() => removeField(field.id)}
              onAddConditionalField={() => addConditionalField(field.id)}
              onUpdateConditionalField={updateField}
              onRemoveConditionalField={removeField}
              onUpdateConditionValue={updateConditionValue}
            />
          ))}
        </div>
      )}

      {/* Save/Reset buttons - only show when there are changes */}
      {editable && hasChanges && (
        <div className="tw:flex tw:items-center tw:gap-2 tw:pt-2 tw:border-t tw:border-border">
          <Button type="button" size="sm" onClick={handleSubmit}>
            <FaSave className="tw:mr-1" />
            {i18n.t('ItemType:saveCustomFields', 'Save Custom Fields')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleReset}
          >
            {i18n.t('Common:reset', 'Reset')}
          </Button>
        </div>
      )}
    </div>
  );
}
